import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/components/atoms/format-date';
import { FilterChips, FilterType } from 'src/components/molecules/filter-chips';
import {
  BookingPresenceFilterValue,
  bookingPresenceFilterValues,
} from 'src/features/bookings/constants/booking-presence/booking-presence-filter-values';
import { bookingPresenceTranslations } from 'src/features/bookings/constants/booking-presence/booking-presence-translations';
import {
  BookingStateFilterValue,
  bookingStateFilterValues,
} from 'src/features/bookings/constants/booking-state/booking-state-filter-values';
import { bookingStateTranslations } from 'src/features/bookings/constants/booking-state/booking-state-translations';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { HandleChangeFilterValue } from 'src/types/handle-change-filter-value';
import { Filters } from 'src/types/interfaces/filters';

interface Props {
  filters: Filters;
  handleChangeFilterValue: HandleChangeFilterValue;
}

export const BookingsFilterChips = ({
  filters,
  handleChangeFilterValue,
}: Props) => {
  const { t } = useTranslation(['bookings', 'spaces']);

  const formatDateValue = (value: number) =>
    formatDate(new Date(value), 'yyyy-MM-dd');

  const findStatusFilterTranslation = (value: BookingStateFilterValue) => {
    if (value === bookingStateFilterValues.BOOKED) {
      return `${t('bookings:common.state.title')}: ${t(bookingStateTranslations.BOOKED)}`;
    }

    if (value === bookingStateFilterValues.STARTED) {
      return `${t('bookings:common.state.title')}: ${t(bookingStateTranslations.STARTED)}`;
    }

    if (value === bookingStateFilterValues.FINISHED) {
      return `${t('bookings:common.state.title')}: ${t(bookingStateTranslations.FINISHED)}`;
    }

    if (value === bookingStateFilterValues.CANCELED) {
      return `${t('bookings:common.state.title')}: ${t(bookingStateTranslations.CANCELED)}`;
    }

    return '';
  };

  const findPresenceFilterTranslation = (value: BookingPresenceFilterValue) => {
    if (value === bookingPresenceFilterValues.PRESENT) {
      return `${t('bookings:common.presence.title')}: ${t(bookingPresenceTranslations.PRESENT)}`;
    }

    return `${t('bookings:common.presence.title')}: ${t(bookingPresenceTranslations.ABSENT)}`;
  };

  const filterTypes: FilterType[] = [
    {
      value: 'status',
      text: value =>
        findStatusFilterTranslation(value as BookingStateFilterValue),
    },
    {
      value: 'spaceType',
      text: value =>
        `${t('bookings:common.spaceType')}: ${t(spaceTypesTranslations[value as SpaceType])}`,
    },
    {
      value: 'spaceFloor',
      text: value => `${t('bookings:common.spaceFloor')}: ${value}`,
    },
    {
      value: 'from',
      text: value =>
        `${t('bookings:common.start')}: ${formatDateValue(Number((value as string[])[0]))} - ${formatDateValue(
          Number((value as string[])[1]),
        )}`,
    },
    {
      value: 'to',
      text: value =>
        `${t('bookings:common.end')}: ${formatDateValue(Number((value as string[])[0]))} - ${formatDateValue(
          Number((value as string[])[1]),
        )}`,
    },
    {
      value: 'presence',
      text: value =>
        findPresenceFilterTranslation(value as BookingPresenceFilterValue),
    },
  ];

  return (
    <FilterChips
      filters={filters}
      handleChangeFilterValue={handleChangeFilterValue}
      filterTypes={filterTypes}
    />
  );
};
